import axios from "axios";
import { env } from "config/env";
import { getForwardingParams } from "domains/auth/queries/helpers";
import type { ExtraState } from "domains/auth/queries/types";
import SuperTokens from "supertokens-web-js";
import Passwordless from "supertokens-web-js/recipe/passwordless";
import Session from "supertokens-web-js/recipe/session";
import ThirdParty from "supertokens-web-js/recipe/thirdparty";
import type { MarkOptional } from "ts-essentials";

let initialized = false;

const getBaseDomain = (domain: string) => new URL(domain).hostname.split(".").slice(-2).join(".");

const domainReplaceLookup: Record<string, string> = {
  "app.gigpro.com": "biz.qwick.com",
  "biz.qwick.com": "app.gigpro.com",
};
const replacementStrings = Object.keys(domainReplaceLookup);

export function initializeAuth() {
  if (initialized) return;
  // e.g. https://gp-api-dev.gigpro.com/ => gigpro.com
  const href = window.location.href;
  const baseApiDomain = getBaseDomain(env.REACT_APP_SG_API_URL);
  const baseWebDomain = getBaseDomain(window.location.origin);

  if (baseApiDomain !== baseWebDomain) {
    // check lookup for domain replacement
    const replacementDomain = replacementStrings.find((domain) => href.includes(domain));
    if (replacementDomain) {
      window.location.href = href.replace(replacementDomain, domainReplaceLookup[replacementDomain]);
      return;
    }
    window.location.href = href.replace(baseWebDomain, baseApiDomain);
    return;
  }

  initialized = true;
  axios.defaults.withCredentials = true;
  SuperTokens.init({
    appInfo: {
      appName: "Gigpro",
      apiDomain: env.REACT_APP_SG_API_URL,
      apiBasePath: "/api/auth",
    },
    recipeList: [
      Session.init({
        // sets the session token domain to the current top-level domain e.g. local.gigpro.com => .gigpro.com
        sessionTokenBackendDomain: `.${baseApiDomain}`,
      }),
      Passwordless.init({ preAPIHook }),
      ThirdParty.init({
        preAPIHook,
        override: {
          functions: (origImpl) => ({
            ...origImpl,
            setStateAndOtherInfoToStorage(input) {
              const modifiedInput = {
                ...input,
                state: { ...input.state, search: window.location.search },
              };
              return origImpl.setStateAndOtherInfoToStorage(modifiedInput);
            },
            async signInAndUp(input) {
              const state = origImpl.getStateAndOtherInfoFromStorage<ExtraState>(input);
              return origImpl.signInAndUp({
                ...input,
                options: { preAPIHook: (context) => preAPIHook(context, getForwardingParams(state?.search)) },
              });
            },
          }),
        },
      }),
    ],
  });
}

const LINK_TO_ACCOUNT = "linkToAccount";
const accountLinkingActions: Array<ThirdParty.PreAndPostAPIHookAction | Passwordless.PreAndPostAPIHookAction> = [
  "PASSWORDLESS_CONSUME_CODE",
  "THIRD_PARTY_SIGN_IN_UP",
];

function preAPIHook(
  { action, requestInit, url }: MarkOptional<ThirdParty.PreAPIHookContext | Passwordless.PreAPIHookContext, "action">,
  forwardParams = getForwardingParams(),
) {
  const linkToAccount = new URLSearchParams(window.location.search).get(LINK_TO_ACCOUNT);

  const apiParams = new URLSearchParams(url.split("?")[1]);

  for (const [key, value] of forwardParams) {
    apiParams.set(key, value);
  }
  if (linkToAccount && action && accountLinkingActions.includes(action)) {
    apiParams.set("link_to_account", linkToAccount);
  }
  return Promise.resolve({ requestInit, url: `${url.split("?")[0]}?${apiParams}` });
}

export async function startAccountLinking() {
  const userId = await Session.getUserId();
  if (!userId) throw new Error("User ID not found");
  await Session.signOut();
  window.location.href = `/auth/?${LINK_TO_ACCOUNT}=${userId}`;
}
